/**
 * Frontend registry for admin audit actions and target types.
 * Keep in sync with functions/admin/auditMetadata.js when Lot 4.3 adds writes.
 * Labels live in LanguageContext; this file only stores values and i18n keys.
 */

export const ADMIN_AUDIT_PAGE_SIZE = 25;

export const ADMIN_AUDIT_FILTER_TYPES = Object.freeze(['all', 'action', 'actor', 'targetType']);

export const ADMIN_AUDIT_SUMMARY_UNKNOWN = 'admin.audit.summary.unknown';

/** @type {{ value: string, summaryKey: string, filterLabelKey: string }[]} */
export const ADMIN_AUDIT_ACTIONS = Object.freeze([
  {
    value: 'role_changed',
    summaryKey: 'admin.audit.summary.roleChanged',
    filterLabelKey: 'admin.audit.filter.action.roleChanged',
  },
  {
    value: 'impersonation_started',
    summaryKey: 'admin.audit.summary.impersonationStarted',
    filterLabelKey: 'admin.audit.filter.action.impersonationStarted',
  },
  {
    value: 'impersonation_ended',
    summaryKey: 'admin.audit.summary.impersonationEnded',
    filterLabelKey: 'admin.audit.filter.action.impersonationEnded',
  },
  {
    value: 'prospect_created',
    summaryKey: 'admin.audit.summary.prospectCreated',
    filterLabelKey: 'admin.audit.filter.action.prospectCreated',
  },
  {
    value: 'prospect_updated',
    summaryKey: 'admin.audit.summary.prospectUpdated',
    filterLabelKey: 'admin.audit.filter.action.prospectUpdated',
  },
  {
    value: 'prospect_projection_verification_updated',
    summaryKey: 'admin.audit.summary.prospectProjectionVerificationUpdated',
    filterLabelKey: 'admin.audit.filter.action.prospectProjectionVerificationUpdated',
  },
  {
    value: 'prospect_converted',
    summaryKey: 'admin.audit.summary.prospectConverted',
    filterLabelKey: 'admin.audit.filter.action.prospectConverted',
  },
    {
      value: 'prospect_deleted',
      summaryKey: 'admin.audit.summary.prospectDeleted',
      filterLabelKey: 'admin.audit.filter.action.prospectDeleted',
    },
    {
      value: 'prospects_imported',
      summaryKey: 'admin.audit.summary.prospectsImported',
      filterLabelKey: 'admin.audit.filter.action.prospectsImported',
    },
    {
      value: 'prospect_campaign_created',
      summaryKey: 'admin.audit.summary.prospectCampaignCreated',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignCreated',
    },
    {
      value: 'prospect_campaign_mailings_prepared',
      summaryKey: 'admin.audit.summary.prospectCampaignMailingsPrepared',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignMailingsPrepared',
    },
    {
      value: 'prospect_public_responses_reconciled',
      summaryKey: 'admin.audit.summary.prospectPublicResponsesReconciled',
      filterLabelKey: 'admin.audit.filter.action.prospectPublicResponsesReconciled',
    },
    {
      value: 'prospect_campaign_letter_archive_requested',
      summaryKey: 'admin.audit.summary.prospectCampaignLetterArchiveRequested',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignLetterArchiveRequested',
    },
    {
      value: 'prospect_campaign_letter_archive_ready',
      summaryKey: 'admin.audit.summary.prospectCampaignLetterArchiveReady',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignLetterArchiveReady',
    },
    {
      value: 'prospect_campaign_letter_archive_failed',
      summaryKey: 'admin.audit.summary.prospectCampaignLetterArchiveFailed',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignLetterArchiveFailed',
    },
    {
      value: 'prospect_campaign_letter_archive_recovered',
      summaryKey: 'admin.audit.summary.prospectCampaignLetterArchiveRecovered',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignLetterArchiveRecovered',
    },
    {
      value: 'prospect_campaign_letter_archive_downloaded',
      summaryKey: 'admin.audit.summary.prospectCampaignLetterArchiveDownloaded',
      filterLabelKey: 'admin.audit.filter.action.prospectCampaignLetterArchiveDownloaded',
    },
    {
      value: 'prospect_letter_template_created',
      summaryKey: 'admin.audit.summary.prospectLetterTemplateCreated',
      filterLabelKey: 'admin.audit.filter.action.prospectLetterTemplateCreated',
    },
    {
      value: 'prospect_letter_template_version_created',
      summaryKey: 'admin.audit.summary.prospectLetterTemplateVersionCreated',
      filterLabelKey: 'admin.audit.filter.action.prospectLetterTemplateVersionCreated',
    },
    {
      value: 'prospect_letter_template_duplicated',
      summaryKey: 'admin.audit.summary.prospectLetterTemplateDuplicated',
      filterLabelKey: 'admin.audit.filter.action.prospectLetterTemplateDuplicated',
    },
    {
      value: 'prospect_letter_template_archived',
      summaryKey: 'admin.audit.summary.prospectLetterTemplateArchived',
      filterLabelKey: 'admin.audit.filter.action.prospectLetterTemplateArchived',
    },
    {
      value: 'prospect_letter_template_asset_uploaded',
      summaryKey: 'admin.audit.summary.prospectLetterTemplateAssetUploaded',
      filterLabelKey: 'admin.audit.filter.action.prospectLetterTemplateAssetUploaded',
    },
  ]);

/** Observed targetType values in the repo. Do not invent types. */
export const ADMIN_AUDIT_TARGET_TYPES = Object.freeze([
  {
    value: 'user',
    labelKey: 'admin.audit.targetType.user',
  },
  {
    value: 'prospect',
    labelKey: 'admin.audit.targetType.prospect',
  },
  {
    value: 'prospect_import',
    labelKey: 'admin.audit.targetType.prospect_import',
  },
  {
    value: 'prospect_campaign',
    labelKey: 'admin.audit.targetType.prospect_campaign',
  },
  {
    value: 'prospect_letter_template',
    labelKey: 'admin.audit.targetType.prospect_letter_template',
  },
  {
    value: 'prospect_letter_template_asset',
    labelKey: 'admin.audit.targetType.prospect_letter_template_asset',
  },
]);

const ACTIONS_BY_VALUE = new Map(ADMIN_AUDIT_ACTIONS.map((item) => [item.value, item]));

/**
 * @param {unknown} action
 * @returns {(typeof ADMIN_AUDIT_ACTIONS)[number] | null}
 */
export function getAdminAuditAction(action) {
  if (typeof action !== 'string') return null;
  return ACTIONS_BY_VALUE.get(action.trim()) || null;
}

/**
 * Prefer the backend summaryKey; fall back to the registry; unknown shares one key.
 * @param {{ action?: unknown, summaryKey?: unknown }} log
 * @returns {string}
 */
export function resolveAdminAuditSummaryKey(log) {
  const fromLog = typeof log?.summaryKey === 'string' ? log.summaryKey.trim() : '';
  if (fromLog) return fromLog;
  return getAdminAuditAction(log?.action)?.summaryKey || ADMIN_AUDIT_SUMMARY_UNKNOWN;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isKnownAdminAuditTargetType(value) {
  if (typeof value !== 'string') return false;
  return ADMIN_AUDIT_TARGET_TYPES.some((item) => item.value === value.trim());
}
